import { useState, useEffect, useRef, useMemo, useCallback, type KeyboardEvent } from 'react'
import {
  useHostTemplates,
  useHostTemplate,
  useHostTemplateSeeds,
  useCreateHostTemplate,
  useUpdateHostTemplate,
  useDeleteHostTemplate,
  useCreateHostAttribute,
  useUpdateHostAttribute,
  useDeleteHostAttribute,
  useReorderHostAttributes,
} from '@/admin/hooks/use-host-templates'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { MasterDetail } from '@/admin/components/shared/master-detail'
import { AttributeDeleteDialog } from '@/admin/components/shared/attribute-delete-dialog'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import {
  AttributeTemplatePicker,
  type AttributeTemplatePickResult,
  type AttributeTemplateResult,
} from '@/admin/components/shared/attribute-template-picker'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import {
  EntityAttributeDialog,
  EntityDisplayTab,
  EntityRegistrationSection,
  EntityActionsTab,
  type DisplayColumnPriority,
} from '@/admin/pages/manage/entities/entity-editor-page'
import { useEntityTypes } from '@/admin/hooks/use-entities'
import { useHashTab, MANAGE_TEMPLATE_TABS } from '@/admin/hooks/use-hash-tab'
import { Plus, Pencil, Trash2, X, GripVertical } from 'lucide-react'
import { CategoryIcon, DEFAULT_ICON_KEY } from '@/admin/components/category-icons'
import { LucideIconPicker } from '@/shared/components/ui/lucide-icon-picker'
import { toast } from 'sonner'
import type { HostAttributeMeta, HostTemplateSeed } from '@/admin/types/hosts'
import type { FieldType } from '@/admin/types/products'
import { cn } from '@/shared/lib/utils'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'

// ── Constants ────────────────────────────────────────────

import { toastApiError } from '@/shared/lib/api-error'
const FIELD_TYPES: FieldType[] = [
  'TEXT', 'RICH_TEXT', 'NUMBER', 'BOOLEAN', 'SELECT', 'MULTI_SELECT',
  'DATE', 'DATE_TIME', 'DOCUMENT', 'FILE', 'IMAGE', 'VIDEO',
  'LINK', 'MONEY', 'ADDRESS', 'PHONE', 'EMAIL',
]

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  TEXT: 'Text',
  RICH_TEXT: 'Rich Text',
  NUMBER: 'Number',
  BOOLEAN: 'Boolean',
  SELECT: 'Select',
  MULTI_SELECT: 'Multi Select',
  DATE: 'Date',
  DATE_TIME: 'Date & Time',
  DOCUMENT: 'Document',
  FILE: 'File',
  IMAGE: 'Image',
  VIDEO: 'Video',
  LINK: 'Link',
  MONEY: 'Money',
  ADDRESS: 'Address',
  PHONE: 'Phone',
  EMAIL: 'Email',
}

function isSelectType(ft: FieldType) {
  return ft === 'SELECT' || ft === 'MULTI_SELECT'
}

/** Options come from the backend as either plain strings or {label, value} objects. Normalise to strings for display. */
function normaliseOptions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.map((o) => {
    if (typeof o === 'string') return o
    if (o && typeof o === 'object' && 'label' in o) return String(o.label)
    if (o && typeof o === 'object' && 'value' in o) return String(o.value)
    return String(o)
  })
}

/** Convert string array back to {label, value} objects for the API. */
function optionsToPayload(opts: string[]): Array<{ label: string; value: string }> {
  return opts.map((o) => ({ label: o, value: o }))
}

// ── Main Page ────────────────────────────────────────────

export function HostTemplatesPage() {
  const { data: templates, isLoading } = useHostTemplates()
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  return (
    <>
      <MasterDetail
        testId="host-templates"
        title="Host Templates"
        onAdd={() => setShowCreateDialog(true)}
        hasSelection={!!selectedUuid}
        selectedId={selectedUuid}
        visibleItemIds={(templates ?? []).map((template) => template.uuid)}
        onSelectionChange={setSelectedUuid}
        onBack={() => setSelectedUuid(null)}
        list={
          <>
            {isLoading ? (
              <p className="text-[12px] text-[var(--ink-3)]">Loading...</p>
            ) : !templates?.length ? (
              <p className="text-[12px] text-[var(--ink-3)]">No host templates yet</p>
            ) : (
              <div className="flex flex-col gap-0.5">
                {templates.map((tmpl) => {
                  const isActive = tmpl.uuid === selectedUuid
                  return (
                    <Button
                      variant="ghost"
                      key={tmpl.uuid}
                      data-testid={`host-templates-item-${tmpl.uuid}`}
                      onClick={() => setSelectedUuid(tmpl.uuid)}
                      className={cn(
                        'flex h-auto w-full cursor-pointer items-center justify-start gap-2 rounded-[4px] px-3 py-2 text-left text-[12px] transition-all duration-150',
                        isActive
                          ? 'bg-[rgba(28,90,72,0.06)] text-[var(--green)]'
                          : 'text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]',
                      )}
                    >
                      <CategoryIcon icon={tmpl.icon || DEFAULT_ICON_KEY} className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{tmpl.name}</span>
                    </Button>
                  );
                })}
              </div>
            )}
          </>
        }
        detail={
          selectedUuid ? (
            <HostTemplateDetail
              templateUuid={selectedUuid}
              onDeleted={() => setSelectedUuid(null)}
            />
          ) : (
            <div className="flex justify-center pt-24">
              <p className="text-[13px] text-[var(--ink-3)]">
                Select a host template to view its attributes
              </p>
            </div>
          )
        }
      />
      {/* Create template dialog */}
      <CreateHostTemplateDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={(uuid) => {
          setSelectedUuid(uuid)
          setShowCreateDialog(false)
        }}
      />
    </>
  );
}

// ── Create Host Template Dialog ──────────────────────────

function CreateHostTemplateDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (uuid: string) => void
}) {
  const { data: seeds, isLoading: seedsLoading } = useHostTemplateSeeds()
  const createTemplate = useCreateHostTemplate()

  const [step, setStep] = useState<'pick' | 'form'>('pick')
  const [selectedSeed, setSelectedSeed] = useState<HostTemplateSeed | null>(null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [icon, setIcon] = useState(DEFAULT_ICON_KEY)

  function reset() {
    setStep('pick')
    setSelectedSeed(null)
    setName('')
    setSlug('')
    setIcon(DEFAULT_ICON_KEY)
  }

  function handleOpenChange(val: boolean) {
    if (!val) reset()
    onOpenChange(val)
  }

  function handleSelectSeed(seed: HostTemplateSeed) {
    setSelectedSeed(seed)
    setName(seed.name)
    setSlug(seed.slug)
    setIcon(seed.icon || DEFAULT_ICON_KEY)
    setStep('form')
  }

  function handleBlankTemplate() {
    setSelectedSeed(null)
    setName('')
    setSlug('')
    setIcon(DEFAULT_ICON_KEY)
    setStep('form')
  }

  function handleNameChange(val: string) {
    setName(val)
    setSlug(
      val
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+$/, ''),
    )
  }

  async function handleCreate() {
    if (!name.trim() || !slug.trim()) return
    try {
      const payload: {
        name: string
        slug: string
        icon?: string
        seed_kind?: string
      } = { name: name.trim(), slug: slug.trim(), icon }
      if (selectedSeed) {
        payload.seed_kind = selectedSeed.seed_kind
      }
      const created = await createTemplate.mutateAsync(payload)
      toast.success('Host template created')
      reset()
      onCreated(created.uuid)
    } catch (err) {
      toastApiError(err, 'Failed to create host template');
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Host Template</DialogTitle>
        </DialogHeader>

        {step === 'pick' ? (
          <div className="py-2">
            <span className="clara-section-label mb-3 block">Start from a seed</span>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {seedsLoading ? (
                <p className="col-span-3 text-[12px] text-[var(--ink-3)]">Loading seeds...</p>
              ) : seeds?.length ? (
                seeds.map((seed) => {
                  return (
                    <Button
                      variant="outline"
                      key={seed.seed_kind}
                      onClick={() => handleSelectSeed(seed)}
                      className="flex flex-col items-center gap-2 px-3 py-4 rounded-[8px] cursor-pointer transition-all duration-150 h-auto border-[0.5px] border-[var(--rule)] bg-[var(--bg)] hover:border-[var(--green)] hover:bg-[var(--green-bg)]"
                    >
                      <CategoryIcon icon={seed.icon || DEFAULT_ICON_KEY} className="h-6 w-6 text-[var(--ink-2)]" />
                      <span className="text-[12px] text-[var(--ink)]">{seed.name}</span>
                      <span className="text-[10px] text-[var(--ink-3)]">
                        {seed.attribute_count} attributes
                      </span>
                    </Button>
                  );
                })
              ) : (
                <p className="col-span-3 text-[12px] text-[var(--ink-3)]">No seeds available</p>
              )}
            </div>

            <div className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
              <Button
                variant="outline"
                onClick={handleBlankTemplate}
                className="h-auto w-full cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] px-3 py-2.5 text-left text-[12px] text-[var(--ink-2)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
              >
                <Plus className="mr-2 inline-block h-3.5 w-3.5 translate-y-[-2px]" />
                Blank template
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-2 flex flex-col gap-4">
            {selectedSeed && (
              <div className="flex items-center gap-2 rounded-[4px] bg-[var(--bg-subtle)] px-3 py-2">
                <CategoryIcon icon={icon} className="h-4 w-4 shrink-0 text-[var(--green)]" />
                <span className="text-[12px] text-[var(--ink-2)]">
                  Seeded from <strong className="font-medium">{selectedSeed.name}</strong> ({selectedSeed.attribute_count} attributes)
                </span>
              </div>
            )}
            <div>
              <Label className="clara-section-label mb-1.5 block">Name</Label>
              <Input
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g. Vehicle"
                className="text-[13px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate()
                }}
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Slug</Label>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. vehicle"
                className="text-[13px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate()
                }}
              />
            </div>
            <LucideIconPicker
              value={icon}
              onChange={setIcon}
              mode="floating"
              label="Icon"
            />
          </div>
        )}

        <DialogFooter>
          {step === 'form' && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => setStep('pick')}
                className="h-auto cursor-pointer rounded-[4px] px-3 py-1.5 text-[11px] text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
              >
                Back
              </Button>
              <GreenButton
                data-testid="host-templates-dialog-submit"
                onClick={handleCreate}
                disabled={!name.trim() || !slug.trim() || createTemplate.isPending}
              >
                Create
              </GreenButton>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Template Detail ──────────────────────────────────────

function HostTemplateDetail({
  templateUuid,
  onDeleted,
}: {
  templateUuid: string
  onDeleted: () => void
}) {
  const [tab, setTab] = useHashTab(MANAGE_TEMPLATE_TABS, "general")
  const { data: template, isLoading: templateLoading } = useHostTemplate(templateUuid)
  const { data: entityTypes = [] } = useEntityTypes()
  const updateTemplate = useUpdateHostTemplate()
  const deleteTemplate = useDeleteHostTemplate()

  const [showAttrDialog, setShowAttrDialog] = useState(false)
  const [showComputedFieldDialog, setShowComputedFieldDialog] = useState(false)
  const [editingAttr, setEditingAttr] = useState<HostAttributeMeta | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  // Prefills the create modal's display name when the user chooses "create new"
  // from the add-attribute picker.
  const [prefillName, setPrefillName] = useState('')
  const addAttr = useCreateHostAttribute()
  const resourceLookupProviders = useResourceLookupProviders()

  // Editable fields
  const [editName, setEditName] = useState('')
  const [editSlug, setEditSlug] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editIcon, setEditIcon] = useState('')
  const [editIdentityAttrs, setEditIdentityAttrs] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  // Sync local state when template loads
  const prevUuidRef = useRef<string | null>(null)
  useEffect(() => {
    if (template && prevUuidRef.current !== templateUuid) {
      prevUuidRef.current = templateUuid
       
      setEditName(template.name)
      setEditSlug(template.slug)
      setEditDescription(template.description || '')
      setEditIcon(template.icon || '')
      setEditIdentityAttrs((template.identity_attributes || []).join(', '))
      setIsDirty(false)
      setDeleteDialogOpen(false)
    }
  }, [template, templateUuid])

  async function handleSave() {
    if (!template) return
    try {
      const identityAttrs = editIdentityAttrs
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)

      await updateTemplate.mutateAsync({
        uuid: template.uuid,
        name: editName,
        slug: editSlug,
        description: editDescription,
        icon: editIcon,
        identity_attributes: identityAttrs,
      })
      setIsDirty(false)
      toast.success('Host template saved')
    } catch (err) {
      toastApiError(err, 'Failed to save host template');
    }
  }

  async function handleDeleteTemplate() {
    if (!template) return
    try {
      await deleteTemplate.mutateAsync(template.uuid)
      toast.success('Host template deleted')
      setDeleteDialogOpen(false)
      onDeleted()
    } catch (err) {
      toastApiError(err, 'Failed to delete host template');
    }
  }

  function markDirty() {
    setIsDirty(true)
  }

  function openEditAttr(attr: HostAttributeMeta) {
    setEditingAttr(attr)
    setShowAttrDialog(true)
  }

  // Add-attribute entry point. Selecting an existing shared attribute attaches
  // it to this template immediately (the backend reuses the bucket definition,
  // so field_type + options stay consistent). Choosing "create new" opens the
  // full attribute modal, prefilled with the typed name.
  async function handleAddAttrPick(result: AttributeTemplatePickResult) {
    if (result.type === 'create') {
      setPrefillName(result.name ?? '')
      setEditingAttr(null)
      setShowAttrDialog(true)
      return
    }
    if (result.type === 'existing') {
      const tpl: AttributeTemplateResult = result.template
      try {
        await addAttr.mutateAsync({
          templateUuid,
          display_name: tpl.display_name,
          code_name: tpl.code_name,
          field_type: tpl.field_type,
          required: false,
          visibility: 'PUBLIC',
          configuration_data: tpl.configuration_data ?? {},
        })
        toast.success('Attribute added')
      } catch (err) {
        toastApiError(err, 'Failed to add attribute')
      }
    }
  }

  if (templateLoading) {
    return <p className="text-[13px] text-[var(--ink-3)]">Loading...</p>;
  }

  if (!template) return null

  const sortedAttributes = [...(template.attributes || [])].sort((a, b) => a.order - b.order)
  const hostEntity = entityTypes.find((entity) => entity.key === 'host')
  const priorityFields: DisplayColumnPriority[] = sortedAttributes.map((attribute) => ({
    codeName: attribute.code_name,
    entityKeys: ['host'],
    hostTemplateSlug: template.slug,
  }))

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
    <div>
      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <TabsList variant="line">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="registration">Registration</TabsTrigger>
          <TabsTrigger value="display">Display</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          {/* Template name + save / delete */}
          <div className="mb-6 flex items-center justify-between">
            <Input
              value={editName}
              onChange={(e) => { setEditName(e.target.value); markDirty() }}
              className="max-w-md border-none px-0 text-[20px] text-[var(--ink)] font-display"
            />
            <div className="flex items-center gap-2">
              {isDirty && (
                <GreenButton onClick={handleSave} disabled={updateTemplate.isPending}>
                  Save
                </GreenButton>
              )}
              <IconButton onClick={() => setDeleteDialogOpen(true)} title="Delete template">
                <Trash2 className="h-3.5 w-3.5" />
              </IconButton>
            </div>
          </div>
          {/* Template metadata fields */}
          <div className="mb-6 grid grid-cols-2 gap-4">
            <div>
              <Label className="clara-section-label mb-1.5 block">Slug</Label>
              <Input
                value={editSlug}
                onChange={(e) => { setEditSlug(e.target.value); markDirty() }}
                className="text-[13px]"
                placeholder="e.g. vehicle"
              />
            </div>
            <div>
              <LucideIconPicker
                value={editIcon}
                onChange={(value) => { setEditIcon(value); markDirty() }}
                mode="floating"
                label="Icon"
              />
            </div>
          </div>
          <div className="mb-6">
            <Label className="clara-section-label mb-1.5 block">Description</Label>
            <Input
              value={editDescription}
              onChange={(e) => { setEditDescription(e.target.value); markDirty() }}
              className="text-[13px]"
              placeholder="A brief description of this host type"
            />
          </div>
          <div className="mb-6">
            <Label className="clara-section-label mb-1.5 block">Identity Attributes</Label>
            <p className="mb-2 text-[11px] text-[var(--ink-3)]">
              Comma-separated attribute code names used to uniquely identify a host (e.g. vrn, vin).
            </p>
            <Input
              value={editIdentityAttrs}
              onChange={(e) => { setEditIdentityAttrs(e.target.value); markDirty() }}
              className="text-[13px]"
              placeholder="vrn, vin"
            />
          </div>
          {/* Seed kind badge */}
          {template.seed_kind && (
            <div className="mb-6">
              <span className="clara-section-label mb-1.5 block">Seed</span>
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-subtle)] px-2.5 py-1 text-[11px] text-[var(--ink-2)]"
              >
                <CategoryIcon icon={editIcon || template.icon || DEFAULT_ICON_KEY} className="h-3 w-3" />
                {template.seed_kind}
              </span>
            </div>
          )}
          {/* Attributes */}
          <div className="mb-4 border-t-[0.5px] border-t-[var(--rule)] pt-6">
            <div className="mb-4 flex items-center justify-between">
              <span className="clara-section-label">Attributes</span>
              <AttributeTemplatePicker
                onPick={handleAddAttrPick}
                resetAfterPick
                asAddButton
                domain="host"
                data-testid="host-templates-add-attribute-picker"
              />
            </div>

            {sortedAttributes.length === 0 ? (
              <p className="py-2 text-[12px] text-[var(--ink-3)]">
                No attributes defined yet
              </p>
            ) : (
              <SortableHostAttributeList
                attrs={sortedAttributes}
                templateUuid={templateUuid}
                onEditAttr={openEditAttr}
              />
            )}
          </div>
        </TabsContent>
        <TabsContent value="registration">
          {hostEntity ? (
            <EntityRegistrationSection
              key={hostEntity.uuid}
              entity={hostEntity}
              allEntities={entityTypes}
            />
          ) : (
            <p className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4 text-[12px] text-[var(--ink-3)]">
              Registration settings are available once the host entity has been seeded.
            </p>
          )}
        </TabsContent>
        <TabsContent value="display">
          {hostEntity ? (
            <EntityDisplayTab
              entity={hostEntity}
              allEntities={entityTypes}
              onAddComputedField={() => setShowComputedFieldDialog(true)}
              sections={['registration_table', 'registration_card']}
              priorityFields={priorityFields}
            />
          ) : (
            <p className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4 text-[12px] text-[var(--ink-3)]">
              Host display settings are available once the host entity has been seeded.
            </p>
          )}
        </TabsContent>
        <TabsContent value="actions">
          {hostEntity ? (
            <EntityActionsTab entityUuid={hostEntity.uuid} entityKey={hostEntity.key} />
          ) : (
            <p className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4 text-[12px] text-[var(--ink-3)]">
              Actions settings are available once the host entity has been seeded.
            </p>
          )}
        </TabsContent>
      </Tabs>
      {/* Attribute create/edit dialog */}
      <HostAttributeDialog
        templateUuid={templateUuid}
        open={showAttrDialog}
        onOpenChange={(val) => {
          setShowAttrDialog(val)
          if (!val) {
            setEditingAttr(null)
            setPrefillName('')
          }
        }}
        editing={editingAttr}
        prefillName={prefillName}
      />
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={`Delete ${template.name}?`}
        description={
          <>
            Delete <strong>{template.name}</strong>? This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        onConfirm={handleDeleteTemplate}
        isPending={deleteTemplate.isPending}
      />
      {hostEntity ? (
        <EntityAttributeDialog
          entity={hostEntity}
          allEntities={entityTypes}
          attribute={undefined}
          open={showComputedFieldDialog}
          onOpenChange={setShowComputedFieldDialog}
          defaultOrder={hostEntity.attributes.length}
          defaults={{ source: 'COMPUTED' }}
        />
      ) : null}
    </div>
    </ResourceLookupContextProvider>
  );
}

// ── Attribute Row ────────────────────────────────────────

function HostAttributeRow({
  attr,
  isLast,
  onEdit,
  templateUuid,
  dragHandleProps,
}: {
  attr: HostAttributeMeta
  isLast: boolean
  onEdit: () => void
  templateUuid: string
  dragHandleProps?: Record<string, unknown>
}) {
  const deleteAttr = useDeleteHostAttribute()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const options = normaliseOptions(attr.configuration_data?.options)

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 sm:gap-3 px-4 py-3 group transition-all duration-100 hover:bg-[var(--bg-subtle)]',
        !isLast && 'border-b-[0.5px] border-b-[var(--rule)]',
      )}
    >
      {dragHandleProps && (
        <div
          className="shrink-0 touch-none cursor-grab text-[var(--ink-3)] active:cursor-grabbing"
          {...dragHandleProps}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-base text-[var(--ink)]">
            {attr.display_name}
          </span>
          {attr.description && (
            <span className="text-[11px] text-[var(--ink-3)]">
              - {attr.description}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-[var(--ink-3)]">
            {attr.code_name}
          </span>
          {isSelectType(attr.field_type) && options.length > 0 && (
            <span className="text-[10px] text-[var(--ink-3)]">
              · {options.length} option{options.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      {/* Badges */}
      <TypeBadge>{FIELD_TYPE_LABELS[attr.field_type]}</TypeBadge>
      {attr.lookup_provider && (
        <span
          className="inline-flex items-center rounded-full bg-[rgba(28,90,72,0.08)] px-2 py-0.5 text-[10px] text-[var(--green)]"
        >
          {attr.lookup_provider} Lookup
        </span>
      )}
      {attr.is_prepopulated && (
        <span
          className="inline-flex items-center rounded-full bg-[rgba(59,130,246,0.08)] px-2 py-0.5 text-[10px] text-[#3b82f6]"
        >
          Auto-filled
        </span>
      )}
      {attr.maps_to_column && (
        <span
          className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] text-[var(--ink-3)]"
        >
          &rarr; {attr.maps_to_column}
        </span>
      )}
      {attr.required && (
        <span className="text-[10px] text-[var(--green)]">Required</span>
      )}
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-30 group-hover:opacity-100 transition-opacity duration-150">
        <IconButton onClick={onEdit} title="Edit attribute" data-testid={`host-templates-edit-${attr.uuid}`}>
          <Pencil className="h-3 w-3" />
        </IconButton>
        <IconButton onClick={() => setDeleteDialogOpen(true)} title="Delete" data-testid={`host-templates-delete-${attr.uuid}`}>
          <Trash2 className="h-3 w-3" />
        </IconButton>
      </div>
      <AttributeDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        attributeName={attr.display_name}
        description={
          <>
            Delete <strong>{attr.display_name}</strong>? This action cannot be undone.
          </>
        }
        successMessage="Attribute deleted"
        isPending={deleteAttr.isPending}
        onDelete={({ force }) =>
          deleteAttr.mutateAsync({
            templateUuid,
            attributeUuid: attr.uuid,
            force,
          })
        }
      />
    </div>
  );
}

// ── Sortable Attribute List ──────────────────────────────

function SortableHostAttributeList({
  attrs,
  templateUuid,
  onEditAttr,
}: {
  attrs: HostAttributeMeta[]
  templateUuid: string
  onEditAttr: (attr: HostAttributeMeta) => void
}) {
  const reorder = useReorderHostAttributes()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = attrs.findIndex((a) => a.uuid === active.id)
      const newIndex = attrs.findIndex((a) => a.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const reordered = [...attrs]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)

      reorder.mutate({
        templateUuid,
        attributes: reordered.map((a) => ({ uuid: a.uuid })),
      })
    },
    [attrs, templateUuid, reorder],
  )

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={attrs.map((a) => a.uuid)} strategy={verticalListSortingStrategy}>
        <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]">
          {attrs.map((attr, idx) => (
            <SortableHostAttributeRow
              key={attr.uuid}
              attr={attr}
              isLast={idx === attrs.length - 1}
              onEdit={() => onEditAttr(attr)}
              templateUuid={templateUuid}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

function SortableHostAttributeRow(props: {
  attr: HostAttributeMeta
  isLast: boolean
  onEdit: () => void
  templateUuid: string
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.attr.uuid,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <HostAttributeRow {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  )
}

// ── Attribute Dialog (Create / Edit) ─────────────────────

function HostAttributeDialog({
  templateUuid,
  open,
  onOpenChange,
  editing,
  prefillName = '',
}: {
  templateUuid: string
  open: boolean
  onOpenChange: (open: boolean) => void
  editing: HostAttributeMeta | null
  prefillName?: string
}) {
  const createAttr = useCreateHostAttribute()
  const updateAttr = useUpdateHostAttribute()
  const isEdit = !!editing

  const [currentFieldType, setCurrentFieldType] = useState<string>(editing?.field_type ?? 'TEXT')
  const [options, setOptions] = useState<string[]>(
    editing ? normaliseOptions(editing.configuration_data?.options) : [],
  )
  const [optionInput, setOptionInput] = useState('')

  function commitOption() {
    const trimmed = optionInput.trim()
    if (trimmed && !options.includes(trimmed)) {
      setOptions([...options, trimmed])
    }
    setOptionInput('')
  }

  function handleOptionKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commitOption()
    }
    if (e.key === 'Backspace' && !optionInput && options.length > 0) {
      setOptions(options.slice(0, -1))
    }
  }

  function removeOption(idx: number) {
    setOptions(options.filter((_, i) => i !== idx))
  }

  const fieldTypeOptions = FIELD_TYPES.map((ft) => ({
    label: FIELD_TYPE_LABELS[ft],
    value: ft,
  }))

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'display_name',
        label: 'Display Name',
        placeholder: 'e.g. Registration Number',
        value: editing?.display_name ?? prefillName,
        autoFocus: true,
        required: 'Display name is required',
        onChange: (value, formContext) => {
          if (!isEdit) {
            formContext?.setFieldValue(
              'code_name',
              String(value ?? '')
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, '')
                .replace(/\s+/g, '_')
                .replace(/_+$/, ''),
            )
          }
        },
      },
      {
        type: 'text',
        name: 'code_name',
        label: 'Code Name',
        placeholder: 'e.g. registration_number',
        value:
          editing?.code_name ??
          prefillName
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, '_')
            .replace(/_+$/, ''),
        required: 'Code name is required',
        disabled: isEdit,
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Optional description for this attribute',
        value: editing?.description ?? '',
      },
      {
        type: 'select',
        name: 'field_type',
        label: 'Field Type',
        value: editing?.field_type ?? 'TEXT',
        options: fieldTypeOptions,
        disabled: isEdit,
        onChange: (value) => setCurrentFieldType(String(value ?? 'TEXT')),
      },
      {
        type: 'text',
        name: 'maps_to_column',
        label: 'Maps to Column',
        placeholder: 'e.g. vrn',
        value: editing?.maps_to_column ?? '',
        disabled: isEdit,
      },
      {
        type: 'text',
        name: 'lookup_provider',
        label: 'Lookup Provider',
        placeholder: 'e.g. DVLA',
        value: editing?.lookup_provider ?? '',
      },
      {
        type: 'checkbox',
        name: 'required',
        label: 'Required',
        value: editing?.required ?? false,
        hint: 'Must be provided when registering a host',
      },
      {
        type: 'checkbox',
        name: 'is_prepopulated',
        label: 'Auto-filled',
        value: editing?.is_prepopulated ?? false,
        hint: 'Value is populated automatically from a lookup provider',
      },
      {
        type: 'checkbox',
        name: 'visible_to_customer',
        label: 'Visible to customer',
        value: (editing?.visibility ?? 'PUBLIC') === 'PUBLIC',
        hint: 'Captured + visible to admins; uncheck to hide from the customer\'s record card.',
      },
    ],
    [editing, isEdit, fieldTypeOptions, prefillName],
  )

  const formName = `host-attr-dialog-${editing?.uuid ?? 'new'}`
  const { Form, ref } = useForm(
    { name: formName, submit: 'footer', fields },
    async (rawValues) => {
      const values = rawValues as Record<string, unknown>
      const fieldType = values.field_type as string
      // Keep the settings this dialog does not edit; replace only its own.
      const configData: Record<string, unknown> = { ...(editing?.configuration_data ?? {}) }
      delete configData.options
      if (isSelectType(fieldType as FieldType) && options.length > 0) {
        configData.options = optionsToPayload(options)
      }

      try {
        if (isEdit && editing) {
          await updateAttr.mutateAsync({
            templateUuid,
            attributeUuid: editing.uuid,
            display_name: (values.display_name as string).trim(),
            description: (values.description as string).trim(),
            required: Boolean(values.required),
            lookup_provider: (values.lookup_provider as string).trim(),
            is_prepopulated: Boolean(values.is_prepopulated),
            visibility: values.visible_to_customer ? 'PUBLIC' : 'INTERNAL',
            configuration_data: configData,
          })
          toast.success('Attribute updated')
        } else {
          await createAttr.mutateAsync({
            templateUuid,
            code_name: (values.code_name as string).trim(),
            display_name: (values.display_name as string).trim(),
            field_type: fieldType,
            required: Boolean(values.required),
            maps_to_column: (values.maps_to_column as string).trim(),
            lookup_provider: (values.lookup_provider as string).trim(),
            is_prepopulated: Boolean(values.is_prepopulated),
            visibility: values.visible_to_customer ? 'PUBLIC' : 'INTERNAL',
            configuration_data: configData,
          })
          toast.success('Attribute added')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, isEdit ? 'Failed to update attribute' : 'Failed to add attribute')
      }
    },
    [editing?.uuid ?? 'new', open, options, fields],
  )

  // Hard-reset form + re-sync field-type/options local state on open: switching
  // which attribute you edit (or picking a new prefill name) would otherwise
  // show the previous target's config.
  useEffect(() => {
    if (!open) return
    const deriveCode = (name: string) =>
      name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+$/, '')
    ref.current?.resetForm({
      values: {
        display_name: editing?.display_name ?? prefillName,
        code_name: editing?.code_name ?? deriveCode(prefillName),
        description: editing?.description ?? '',
        field_type: editing?.field_type ?? 'TEXT',
        maps_to_column: editing?.maps_to_column ?? '',
        lookup_provider: editing?.lookup_provider ?? '',
        required: editing?.required ?? false,
        is_prepopulated: editing?.is_prepopulated ?? false,
        visible_to_customer: (editing?.visibility ?? 'PUBLIC') === 'PUBLIC',
      },
    })
    setCurrentFieldType(editing?.field_type ?? 'TEXT')
    setOptions(editing ? normaliseOptions(editing.configuration_data?.options) : [])
    setOptionInput('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.uuid, prefillName])

  const activeFieldType = currentFieldType as FieldType

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          const formRef = ref.current
          formRef?.resetForm()
          setCurrentFieldType(editing?.field_type ?? 'TEXT')
          setOptions(editing ? normaliseOptions(editing.configuration_data?.options) : [])
          setOptionInput('')
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Attribute' : 'Add Attribute'}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <Form className="flex flex-col gap-4" />

          {isSelectType(activeFieldType) && (
            <div>
              <Label className="clara-section-label mb-1.5 block">Options</Label>
              <div
                className="flex min-h-[38px] cursor-text flex-wrap items-center gap-1.5 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-3 py-2"
                onClick={() => document.getElementById('host-option-input')?.focus()}
              >
                {options.map((opt, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[11px] text-[var(--ink-2)]"
                  >
                    {opt}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); removeOption(idx) }}
                      className="h-auto w-auto cursor-pointer p-0 text-[var(--ink-3)]"
                    >
                      <X className="h-2.5 w-2.5" />
                    </Button>
                  </span>
                ))}
                <input
                  id="host-option-input"
                  type="text"
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyDown={handleOptionKeyDown}
                  onBlur={commitOption}
                  placeholder={options.length === 0 ? 'Type and press Enter or comma' : ''}
                  className="min-w-[120px] flex-1 bg-transparent text-[13px] text-[var(--ink)] outline-none"
                />
              </div>
              <p className="mt-1 text-[10px] text-[var(--ink-3)]">
                Press Enter or comma to add an option. Backspace removes the last one.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <GreenButton
            type="submit"
            form={formName}
            disabled={createAttr.isPending || updateAttr.isPending}
          >
            {isEdit ? 'Save' : 'Add'}
          </GreenButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Shared UI helpers ────────────────────────────────────

function GreenButton({
  children,
  onClick,
  disabled,
  type,
  form,
  'data-testid': dataTestId,
}: {
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit'
  form?: string
  'data-testid'?: string
}) {
  return (
    <Button
      type={type}
      form={form}
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      className="px-4 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer disabled:opacity-40 h-auto bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
    >
      {children}
    </Button>
  )
}

function TypeBadge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]"
    >
      {children}
    </span>
  );
}

function IconButton({
  children,
  onClick,
  title,
  danger,
  'data-testid': dataTestId,
}: {
  children: React.ReactNode
  onClick: () => void
  title: string
  danger?: boolean
  'data-testid'?: string
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      title={title}
      data-testid={dataTestId}
      className={cn(`flex h-6 w-6 cursor-pointer items-center justify-center rounded-[4px] transition-all duration-150 ${
        danger ? 'hover:bg-[rgba(220,38,38,0.08)]' : 'hover:bg-[var(--bg-subtle)]'
      }`, danger ? 'text-[#dc2626]' : 'text-[var(--ink-3)]')}
    >
      {children}
    </Button>
  )
}
